import React, { useCallback, useEffect, useState, useMemo } from 'react';
import styled from 'styled-components';
import { useClient } from 'shared/apollo';
import {
  FeedbackCommentBodyWithIndex,
  INDICATOR_DETAILS_QUESTION_ID,
} from '../utils';
import {
  CommentsByStageIndicatorDetails,
  getExtraIndicatorDetails,
  getIndicatorDetailsDescription,
  getIndicatorDetailsId,
  getIndicatorDetailsItemTitle,
  getIndicatorDetailsTitle,
  indicatorDetailsKeys,
  parseCommentDetailsWithIndicatorDetails,
  parseImpactDetailsAnswer,
} from 'donor/portal/pages/FeedbackDashboard/Feedback/FeedbackImpactDetails';
import { RenderCommentsProps } from './RenderComments';
import {
  IndicatorSectionKey,
  indicatorSectionsInOrder,
} from '../../ReportBuilder/Components/ImpactIndicator/utils';
import ImpactDetailStageIcons, {
  GenericFeedbackProps,
  ImpactDetailStageIconsProps,
} from 'donor/portal/pages/FeedbackDashboard/Feedback/FeedbackImpactDetails/ImpactDetailStageIcons';
import {
  IndicatorDetails,
  SectionIndicators,
} from '../../ReportBuilder/Components/IndicatorDetails/utils';
import { FeedbackCommentProps } from 'donor/portal/pages/FeedbackDashboard/Feedback/utils';
import { toUserType } from 'donor/portal/pages/FeedbackDashboard/Feedback/api';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import FeedbackVerificationButtons, {
  FeedbackVerificationButtonsProps,
} from './FeedbackVerificationButtons';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import ImpactDetailsEdit from './ImpactDetailsEdit';
import { updateAnswer } from '../MakeEdits/api';
import { useGraphQLRequest } from 'shared/auth/hooks/useApiRequest';
import TrueImpactLoading from 'shared/components/TrueImpactLoading';

export type FeedbackResolved<T> = {
  [x in IndicatorSectionKey]?:
    | undefined
    | {
        [indicatorId: number]:
          | undefined
          | {
              [subQuestion in keyof T]?: boolean;
            };
      };
};

type RenderBottomButtonsProps = Omit<
  FeedbackVerificationButtonsProps,
  'handleEditClick' | 'handleVerifiedClick'
> & {
  isSectionCompleted: boolean;
  resolved: FeedbackResolved<IndicatorDetails>;
  handleEditClick: (
    sectionKey: IndicatorSectionKey,
    item: IndicatorDetails,
    itemKey: keyof IndicatorDetails,
  ) => unknown;
  handleVerifiedClick: (
    sectionKey: IndicatorSectionKey,
    item: IndicatorDetails,
    itemKey: keyof IndicatorDetails,
  ) => unknown;
};

const FeedbackRenderBottomButtons: React.FC<RenderBottomButtonsProps &
  GenericFeedbackProps & {
    itemKey: keyof IndicatorDetails;
    item: IndicatorDetails;
    index: number;
    totalItems: number;
  }> = ({
  item,
  itemKey,
  requestState,
  className,
  index,
  totalItems,
  sectionKey,
  allowEdits,
  resolved,
  isSectionCompleted,
  feedbackStatus,
  handleEditClick,
  handleVerifiedClick,
}) => {
  const isCompleted =
    isSectionCompleted || resolved[sectionKey]?.[item.indicatorId]?.[itemKey];
  return (
    <FeedbackVerificationButtons
      allowEdits={allowEdits}
      feedbackStatus={feedbackStatus}
      isCompleted={isCompleted}
      handleEditClick={() => handleEditClick(sectionKey, item, itemKey)}
      handleVerifiedClick={() => handleVerifiedClick(sectionKey, item, itemKey)}
      requestState={requestState}
      className={index === totalItems - 1 ? className : `${className} margin`}
    />
  );
};

type Options = {
  [x in IndicatorSectionKey]?:
    | undefined
    | {
        [indicatorId: number]:
          | undefined
          | {
              keys: (keyof IndicatorDetails)[];
              indicator: IndicatorDetails;
            };
        options: IndicatorDetails[];
      };
};

const getOptions = (
  answeredIndicators: SectionIndicators | undefined,
  commentsByStage: CommentsByStageIndicatorDetails,
): { options: Options; total: number } => {
  let total = 0;

  const options: Options = {};

  if (!answeredIndicators) return { options, total };

  for (const sectionKey of indicatorSectionsInOrder) {
    const indicators = answeredIndicators[sectionKey];
    for (const o of indicators) {
      for (const k of indicatorDetailsKeys()) {
        const comments: FeedbackCommentProps[] =
          commentsByStage[sectionKey]?.[o.indicatorId]?.[k] || [];
        if (comments.length > 0) {
          if (!options[sectionKey]) {
            options[sectionKey] = {
              options: [o],
            };
          } else {
            if (
              options[sectionKey]!.options.findIndex(
                x => x.indicatorId === o.indicatorId,
              ) === -1
            ) {
              options[sectionKey]!.options.push(o);
            }
          }
          if (!options[sectionKey]![o.indicatorId]) {
            options[sectionKey]![o.indicatorId] = {
              keys: [],
              indicator: o,
            };
          }
          options[sectionKey]![o.indicatorId]!.keys.push(k);
          total += 1;
        }
      }
    }
  }

  return { options, total };
};

const parseFeedbackCommentsWithDetails = (
  comments: FeedbackCommentBodyWithIndex[],
) => {
  const result: CommentsByStageIndicatorDetails = {};
  for (const c of comments) {
    const details = parseCommentDetailsWithIndicatorDetails(c.details);
    if (!details) continue;
    const comment: FeedbackCommentProps = {
      commentId: c.commentId,
      userId: c.userId,
      createdAt: c.createdAt,
      name: c.userName,
      text: c.comment,
      accountType: toUserType(c.userType),
      details: c.details,
      email: c.userEmail,
      updatedAt: c.createdAt,
    };
    if (!result[details.sectionKey]) {
      result[details.sectionKey] = {};
    }
    if (!result[details.sectionKey][details.indicatorId]) {
      result[details.sectionKey][details.indicatorId] = {};
    }
    if (!result[details.sectionKey][details.indicatorId][details.subQuestion]) {
      result[details.sectionKey][details.indicatorId][details.subQuestion] = [];
    }
    result[details.sectionKey][details.indicatorId][details.subQuestion].push(
      comment,
    );
  }
  return result;
};

export interface FeedbackImpactDetailsProps
  extends RenderCommentsProps,
    FeedbackVerificationButtonsProps {
  answer: string | undefined;
}

const StyledDiv = styled.div`
  .feedback-verification-buttons.margin {
    margin-bottom: ${SPACERS.medium};
  }
`;

let timeout: number | undefined = undefined;
const FeedbackImpactDetails: React.FC<FeedbackImpactDetailsProps> = ({
  answer,
  feedback,
  reportId,
  currentUserEmail,
  currentUserId,
  currentUserName,
  currentUserType,
  allowEdits,
  feedbackStatus,
  requestState,
  isSectionCompleted,
  selectedSection,
  handleEditClick,
  handleVerifiedClick,
  deleteQuestionLevelComment,
  handleDeleteComment,
  handleUpdateComment,
  updateQuestionLevelComment,
}) => {
  const [saveRequestState, makeSaveRequest] = useGraphQLRequest<unknown>();
  const [edit, setEdit] = useState<{
    sectionKey: IndicatorSectionKey;
    item: IndicatorDetails;
    itemKey: keyof IndicatorDetails;
  }>();
  const client = useClient();
  const [answeredIndicators, setAnsweredIndicators] = useState<
    SectionIndicators | undefined
  >(parseImpactDetailsAnswer(answer));
  const [commentsByStage, setCommentsByStage] = useState<
    CommentsByStageIndicatorDetails
  >(parseFeedbackCommentsWithDetails(feedback));

  useEffect(() => {
    setAnsweredIndicators(parseImpactDetailsAnswer(answer));
  }, [answer, setAnsweredIndicators]);

  const [totalResolved, setTotalResolved] = useState<number>(0);
  const [resolved, setResolved] = useState<FeedbackResolved<IndicatorDetails>>(
    {},
  );

  const { options, total } = useMemo(
    () => getOptions(answeredIndicators, commentsByStage),
    [answeredIndicators, commentsByStage],
  );

  useEffect(() => {
    if (!answeredIndicators) return;
    makeSaveRequest(
      updateAnswer(client!, {
        reportId,
        questionId: INDICATOR_DETAILS_QUESTION_ID,
        answerId: undefined,
        answer: JSON.stringify(answeredIndicators),
      }),
    );
  }, [answeredIndicators]);

  useEffect(() => {
    if (total && total === totalResolved) {
      handleVerifiedClick();
    }
  }, [total, totalResolved, handleVerifiedClick]);

  const handleCloseModal = useCallback(() => {
    setEdit(undefined);
  }, [setEdit]);

  const handleUpdate = useCallback(
    (
      sectionKey: IndicatorSectionKey,
      item: IndicatorDetails,
      itemKey: keyof IndicatorDetails,
      answer: IndicatorDetails[keyof IndicatorDetails],
    ) => {
      setAnsweredIndicators(s => {
        if (!s) return s;
        const newState: SectionIndicators = { ...s };
        const i = newState?.[sectionKey]?.find(
          x => x.indicatorId === item.indicatorId,
        );
        if (!i) return s;
        (i[itemKey] as typeof answer) = answer;
        return newState;
      });
    },
    [setAnsweredIndicators],
  );

  const handleEdit = useCallback(
    (
      sectionKey: IndicatorSectionKey,
      item: IndicatorDetails,
      itemKey: keyof IndicatorDetails,
    ) => {
      setEdit({
        sectionKey,
        item,
        itemKey,
      });
    },
    [setEdit],
  );

  const handleVerified = useCallback(
    (
      sectionKey: IndicatorSectionKey,
      item: IndicatorDetails,
      itemKey: keyof IndicatorDetails,
    ) => {
      setResolved(s => {
        const newState = { ...s };
        if (!newState[sectionKey]) {
          newState[sectionKey] = {};
        }
        if (!newState[sectionKey]![item.indicatorId]) {
          newState[sectionKey]![item.indicatorId] = {};
        }
        newState[sectionKey]![item.indicatorId]![itemKey] = true;
        return newState;
      });
      setTotalResolved(n => n + 1);
    },
    [setResolved, setTotalResolved],
  );

  const getComments: ImpactDetailStageIconsProps<
    IndicatorDetails
  >['getComments'] = useCallback(
    ({ sectionKey, indicatorId, subQuestion }): FeedbackCommentProps[] =>
      commentsByStage[sectionKey]?.[indicatorId]?.[subQuestion] || [],
    [commentsByStage],
  );

  if (!answer) {
    timeout = window.setTimeout(() => {
      handleEditClick();
    }, 6000);
    return <TrueImpactLoading />;
  }
  timeout !== undefined && window.clearTimeout(timeout);

  if (!answeredIndicators) {
    return (
      <TrueImpactText variant="body-bold" color="critical">
        Impact model details were not saved properly. Please contact support
        about this report.
      </TrueImpactText>
    );
  }

  return (
    <StyledDiv>
      {indicatorSectionsInOrder.map(key => {
        const o = options[key] || {
          options: [],
        };
        const getKeys = (
          k: IndicatorSectionKey,
          indicator: IndicatorDetails,
        ): (keyof IndicatorDetails)[] => o[indicator.indicatorId]?.keys || [];
        return (
          <ImpactDetailStageIcons<IndicatorDetails, RenderBottomButtonsProps>
            key={key}
            itemTitle={getIndicatorDetailsItemTitle}
            reportId={reportId}
            questionId={INDICATOR_DETAILS_QUESTION_ID}
            currentUserName={currentUserName}
            currentUserId={currentUserId}
            currentUserType={currentUserType}
            currentUserEmail={currentUserEmail}
            sectionId="indicator_details"
            sectionKey={key}
            options={o.options}
            getIndicatorId={getIndicatorDetailsId}
            getTitle={getIndicatorDetailsTitle}
            getComments={getComments}
            getDescription={getIndicatorDetailsDescription}
            getExtraDetails={getExtraIndicatorDetails}
            setCommentsByStage={setCommentsByStage}
            getKeys={getKeys}
            IndicatorChildComponent={{
              props: {
                allowEdits,
                feedbackStatus,
                requestState,
                className: 'feedback-verification-buttons',
                handleEditClick: handleEdit,
                handleVerifiedClick: handleVerified,
                resolved,
                isSectionCompleted,
              },
              component: FeedbackRenderBottomButtons,
            }}
          />
        );
      })}
      {edit && (
        <ImpactDetailsEdit
          reportId={reportId}
          open={true}
          item={edit.item}
          itemKey={edit.itemKey}
          sectionKey={edit.sectionKey}
          handleUpdate={handleUpdate}
          handleVerified={handleVerified}
          handleCloseModal={handleCloseModal}
        />
      )}
    </StyledDiv>
  );
};

export default FeedbackImpactDetails;
